import Dialog from "@rio-cloud/rio-uikit/Dialog";
import DatePicker from "@rio-cloud/rio-uikit/DatePicker";
import { Select } from "@rio-cloud/rio-uikit";
import type { SelectOption } from "@rio-cloud/rio-uikit";
import { ReactNode, useEffect, useMemo, useState } from "react";
import moment, { type Moment } from "moment";
import { useIntl } from "react-intl";
import { useDebounce, useT } from "@hooks";
import AuditDialogSkeleton from "@components/Skeletons/AuditDialogSkeleton";
import TransactionSummary, {
  SUMMARY_ICON_STYLE,
  SUMMARY_TEXT_STYLE,
  type SummaryField,
} from "@components/TransactionSummary";
import { getAuditTransactionDetails, getAuditTransactionGroupedChanges } from "@services/audit";
import { getAllPaymentMethods } from "@services/form";
import { resolvePaymentMethodLabel, type PaymentMethodOption } from "@utils/paymentMethodLabel";
import { getLocalizedName } from "@utils/localizedName";
import { getAuditStatusLabelKey } from "@utils/transactionStatus";
import {
  EMPTY_VALUE,
  TimelineRow,
  buildAmountSummaryColumn,
  buildPartySummaryColumns,
  buildStatusSummaryColumn,
  formatText,
} from "../transactionDetails";
import type { AuditChangeGroup, AuditGroupedChangesFilters, AuditTransactionDetails } from "../../../types/audit";

interface Props {
  show: boolean;
  onClose: () => void;
  entityLabel: string;
  transactionId?: string | null;
}

const CELL_PADDING = 10;
const CELL_BORDER = 1;
const CHEVRON_GAP = 2;
const CHEVRON_CIRCLE_SIZE = CELL_PADDING * 2 + CELL_BORDER - CHEVRON_GAP * 2;
const CHEVRON_GLYPH_SIZE = 12;

const CHEVRON_STYLE = {
  width: CHEVRON_CIRCLE_SIZE,
  height: CHEVRON_CIRCLE_SIZE,
  left: -(CHEVRON_GAP + CHEVRON_CIRCLE_SIZE),
  top: "50%",
  transform: "translateY(-50%)",
} as const;

const CHEVRON_GLYPH_STYLE = { ...SUMMARY_ICON_STYLE, fontSize: CHEVRON_GLYPH_SIZE, padding: 0 } as const;

const GROUPS_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE = 400;
const STATUS_FIELD_NAME = "status";
const PAYMENT_METHOD_FIELD_NAME = "paymentmethod";

const formatDateTime = (value?: string | null): string => {
  const parsed = moment(value);
  return value && parsed.isValid() ? parsed.format("DD/MM/YYYY - HH:mm") : EMPTY_VALUE;
};

const toApiDate = (value?: Moment | string): string | undefined => {
  if (!value) return undefined;

  const parsed = typeof value === "string" ? moment(value, "DD/MM/YYYY", true) : value;
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : undefined;
};

const resolveMomentLocale = (locale: string): string => {
  if (locale.startsWith("en")) return "en-gb";
  if (locale.startsWith("es")) return "es";
  return "pt-br";
};

const buildFilterOptions = (groups: AuditChangeGroup[]) => {
  const autores = new Set<string>();
  const campos = new Map<string, string>();

  groups.forEach((group) => {
    const autor = String(group.changedBy ?? "").trim();
    if (autor) autores.add(autor);

    group.changes.forEach((change) => {
      if (change.fieldName) campos.set(change.fieldName, change.fieldLabel || change.fieldName);
    });
  });

  return {
    changedBy: Array.from(autores),
    fields: Array.from(campos.entries()).map(([id, label]) => ({ id, label })),
  };
};

const DialogAudit = ({ show, onClose, entityLabel, transactionId }: Props) => {
  const t = useT();
  const { locale } = useIntl();
  const momentLocale = resolveMomentLocale(locale);

  const [details, setDetails] = useState<AuditTransactionDetails | null>(null);
  const [groups, setGroups] = useState<AuditChangeGroup[]>([]);
  const [filterOptions, setFilterOptions] = useState<{ changedBy: string[]; fields: SelectOption[] }>({
    changedBy: [],
    fields: [],
  });
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [page, setPage] = useState(0);
  const [isLastPage, setIsLastPage] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodOption[]>([]);

  const [search, setSearch] = useState("");
  const [changedByFilter, setChangedByFilter] = useState("");
  const [fieldFilter, setFieldFilter] = useState("");
  const [startDate, setStartDate] = useState<Moment | string>("");
  const [endDate, setEndDate] = useState<Moment | string>("");

  const clearFilters = () => {
    setSearch("");
    setChangedByFilter("");
    setFieldFilter("");
    setStartDate("");
    setEndDate("");
  };

  useEffect(() => {
    if (!show) {
      setDetails(null);
      setGroups([]);
      setPage(0);
      setIsLastPage(true);
      setHasError(false);
      setFilterOptions({ changedBy: [], fields: [] });
      clearFilters();
    }
  }, [show]);

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE);

  const filters: AuditGroupedChangesFilters = useMemo(
    () => ({
      searchTerm: debouncedSearch.trim() || undefined,
      changedBy: changedByFilter || undefined,
      fieldName: fieldFilter || undefined,
      startDate: toApiDate(startDate),
      endDate: toApiDate(endDate),
    }),
    [debouncedSearch, changedByFilter, fieldFilter, startDate, endDate]
  );

  const hasActiveFilters = Object.values(filters).some(Boolean);

  useEffect(() => {
    if (!show || !transactionId) return;

    let isActive = true;

    const loadDetails = async () => {
      try {
        const response = await getAuditTransactionDetails(transactionId);
        if (isActive) setDetails(response);
      } catch {
        if (isActive) setDetails(null);
      }
    };

    loadDetails();

    return () => {
      isActive = false;
    };
  }, [show, transactionId]);

  useEffect(() => {
    if (!show) return;

    let isActive = true;

    const loadPaymentMethods = async () => {
      try {
        const data = await getAllPaymentMethods();
        if (!isActive) return;
        setPaymentMethods(
          data.map((item: any) => ({ id: String(item.id), label: getLocalizedName(item, locale), name: item.name }))
        );
      } catch {
        if (isActive) setPaymentMethods([]);
      }
    };

    loadPaymentMethods();

    return () => {
      isActive = false;
    };
  }, [show, locale]);

  useEffect(() => {
    if (!show || !transactionId) return;

    let isActive = true;

    const loadGroups = async () => {
      setIsLoadingGroups(true);
      setHasError(false);

      try {
        const response = await getAuditTransactionGroupedChanges(transactionId, {
          page: 0,
          size: GROUPS_PAGE_SIZE,
          ...filters,
        });

        if (!isActive) return;

        setGroups(response.content);
        setPage(response.page);
        setIsLastPage(response.last);

        if (!hasActiveFilters) {
          setFilterOptions(buildFilterOptions(response.content));
        }
      } catch {
        if (!isActive) return;

        setGroups([]);
        setIsLastPage(true);
        setHasError(true);
      } finally {
        if (isActive) setIsLoadingGroups(false);
      }
    };

    loadGroups();

    return () => {
      isActive = false;
    };
  }, [show, transactionId, filters, hasActiveFilters]);

  const handleLoadMore = async () => {
    if (!transactionId || isLastPage || isLoadingMore) return;

    setIsLoadingMore(true);

    try {
      const response = await getAuditTransactionGroupedChanges(transactionId, {
        page: page + 1,
        size: GROUPS_PAGE_SIZE,
        ...filters,
      });

      setGroups((prev) => [...prev, ...response.content]);
      setPage(response.page);
      setIsLastPage(response.last);
    } catch {
      setHasError(true);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const formatChangeValue = (fieldName: string, value?: string | null): string => {
    const normalizedFieldName = fieldName?.trim().toLowerCase();

    if (normalizedFieldName === STATUS_FIELD_NAME) {
      const labelKey = getAuditStatusLabelKey(details?.module, value);
      if (labelKey) return t(labelKey);
    }

    if (normalizedFieldName === PAYMENT_METHOD_FIELD_NAME) {
      const label = resolvePaymentMethodLabel(value, t, paymentMethods);
      if (label) return label;
    }

    return formatText(value);
  };

  const summaryColumns: SummaryField[][] = [
    buildStatusSummaryColumn(t, details, details?.module),
    buildAmountSummaryColumn(t, details),
    ...buildPartySummaryColumns(t, details),
  ];

  const renderDeletedNotice = () => {
    if (!details?.deleted) return null;

    const date = formatDateTime(details.deletedAt);

    return (
      <div
        className="display-flex align-items-center gap-10 padding-15 rounded margin-bottom-15"
        style={{ backgroundColor: "var(--brand-danger-decent)" }}
        data-testid="audit-deleted-notice"
      >
        <span className="rioglyph rioglyph-trash text-color-danger text-size-16" aria-hidden="true" />
        <span className="text-medium text-color-danger">
          {details.deletedBy
            ? t("expense.audit.deleted.notice", { date, user: details.deletedBy })
            : t("expense.audit.deleted.noticeWithoutUser", { date })}
        </span>
      </div>
    );
  };

  const renderValueBox = (value: ReactNode) => (
    <div className="bg-lightest rounded padding-x-10 padding-y-5 text-size-14" style={SUMMARY_TEXT_STYLE}>
      {value}
    </div>
  );

  const renderChevronValue = (value: ReactNode) => (
    <div className="position-relative">
      <span
        className="position-absolute bg-lightest rounded-circle display-flex align-items-center justify-content-center"
        style={CHEVRON_STYLE}
        aria-hidden="true"
      >
        <span className="rioglyph rioglyph-chevron-right" style={CHEVRON_GLYPH_STYLE} />
      </span>
      {renderValueBox(value)}
    </div>
  );

  const renderCell = (label: string, icon: string | null, content: ReactNode, isFirst = false) => (
    <div
      className={`flex-1-1 padding-10 width-100pct-xs ${isFirst ? "" : "border border-left-only border-color-light"}`}
      style={{ minWidth: 0 }}
    >
      <div className="display-flex align-items-center gap-5 text-medium text-size-14" style={SUMMARY_TEXT_STYLE}>
        {icon && <span className={`rioglyph ${icon}`} style={SUMMARY_ICON_STYLE} aria-hidden="true" />}
        <span>{label}:</span>
      </div>
      <div className="display-flex flex-column gap-10 margin-top-10">{content}</div>
    </div>
  );

  return (
    <Dialog
      show={show}
      bsSize="lg"
      title={<span className="text-medium text-size-16">{t("expense.dialog.auditTitle")}</span>}
      subtitle={
        <span className="text-size-14 text-color-gray">
          {t(details?.module === "RECEITA" ? "receipt.audit.subtitle" : "expense.audit.subtitle")}
        </span>
      }
      body={
        isLoadingGroups && !hasActiveFilters && groups.length === 0 ? (
          <AuditDialogSkeleton />
        ) : (
          <>
            {renderDeletedNotice()}
            <TransactionSummary entityLabel={entityLabel} columns={summaryColumns} />

            <div className="display-flex flex-wrap align-items-center gap-10 margin-top-15">
              <div className="input-group width-250 width-100pct-xs">
                <span className="input-group-addon">
                  <span className="rioglyph rioglyph-search" aria-hidden="true" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("audit.filter.search")}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>

              <div className="width-200 width-100pct-xs">
                <Select
                  id="auditChangedBy"
                  options={filterOptions.changedBy.map((autor) => ({ id: autor, label: autor }))}
                  value={changedByFilter ? [changedByFilter] : []}
                  placeholder={t("audit.filter.changedBy")}
                  onChange={(option) => setChangedByFilter(option?.id ?? "")}
                  useClear
                />
              </div>

              <div className="width-200 width-100pct-xs">
                <Select
                  id="auditField"
                  options={filterOptions.fields}
                  value={fieldFilter ? [fieldFilter] : []}
                  placeholder={t("audit.filter.field")}
                  onChange={(option) => setFieldFilter(option?.id ?? "")}
                  useClear
                />
              </div>

              <div className="width-150 width-100pct-xs">
                <DatePicker
                  id="auditStartDate"
                  dateFormat="DD/MM/YYYY"
                  className="rounded-small margin-bottom-0"
                  locale={momentLocale}
                  timeFormat={false}
                  value={startDate}
                  onChange={(value) => setStartDate(value)}
                  inputProps={{ placeholder: t("common.placeholder.selectDate") }}
                />
              </div>

              <span className="text-size-14 text-color-gray">{t("audit.filter.until")}</span>

              <div className="width-150 width-100pct-xs">
                <DatePicker
                  id="auditEndDate"
                  dateFormat="DD/MM/YYYY"
                  className="rounded-small margin-bottom-0"
                  locale={momentLocale}
                  timeFormat={false}
                  value={endDate}
                  onChange={(value) => setEndDate(value)}
                  inputProps={{ placeholder: t("common.placeholder.selectDate") }}
                />
              </div>

              <button type="button" className="btn btn-primary btn-outline width-100pct-xs" onClick={clearFilters}>
                {t("audit.filter.clear")}
              </button>
            </div>

            <div className="margin-top-15">
              {isLoadingGroups && <AuditDialogSkeleton items={3} showHeader={false} />}

              {!isLoadingGroups && hasError && (
                <div className="text-center text-color-danger padding-15">{t("expense.audit.message.loadError")}</div>
              )}

              {!isLoadingGroups && !hasError && groups.length === 0 && (
                <div className="text-center text-color-gray padding-15">{t("expense.audit.message.empty")}</div>
              )}

              {!isLoadingGroups &&
                groups.map((group, index) => {
                  const isLast = index === groups.length - 1;

                  return (
                    <TimelineRow
                      key={`${group.timestamp}-${group.changedBy ?? ""}`}
                      isLast={isLast}
                      glyph={<span className="rioglyph rioglyph-ok text-bold" aria-hidden="true" />}
                    >
                      {renderCell(
                        t("expense.audit.label.dateTime"),
                        "rioglyph-calendar",
                        <div className="text-center text-size-14" style={SUMMARY_TEXT_STYLE}>
                          {formatDateTime(group.timestamp)}
                        </div>,
                        true
                      )}

                      {renderCell(
                        t(
                          group.changes.length > 1
                            ? "expense.audit.label.changedFields"
                            : "expense.audit.label.changedField"
                        ),
                        "rioglyph-detail-view",
                        group.changes.map((change) => (
                          <div key={`${change.id}-field`}>{renderValueBox(formatText(change.fieldLabel))}</div>
                        ))
                      )}

                      {renderCell(
                        t("expense.audit.label.previousValue"),
                        null,
                        group.changes.map((change) => (
                          <div key={`${change.id}-old`}>
                            {renderChevronValue(formatChangeValue(change.fieldName, change.oldValue))}
                          </div>
                        ))
                      )}

                      {renderCell(
                        t("expense.audit.label.newValue"),
                        null,
                        group.changes.map((change) => (
                          <div key={`${change.id}-new`}>
                            {renderChevronValue(formatChangeValue(change.fieldName, change.newValue))}
                          </div>
                        ))
                      )}

                      {renderCell(
                        t("expense.audit.label.changedBy"),
                        "rioglyph-user",
                        renderValueBox(formatText(group.changedBy))
                      )}
                    </TimelineRow>
                  );
                })}

              {!isLoadingGroups && !isLastPage && (
                <div className="display-flex justify-content-center margin-top-15">
                  <button
                    type="button"
                    className="btn btn-primary btn-outline"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? t("common.label.loading") : t("common.action.loadMore")}
                  </button>
                </div>
              )}
            </div>
          </>
        )
      }
      footer={
        <div className="display-flex justify-content-end">
          <button type="button" className="btn btn-default" onClick={onClose}>
            {t("common.action.close")}
          </button>
        </div>
      }
      onClose={onClose}
      showCloseButton
      useOverflow
    />
  );
};

export default DialogAudit;
